<template>
  <b-list-group-item
    class="border-0 service-item"
    :class="{ 'drop-zone-active': isDragOver }"
  >
    <div class="d-flex align-items-center">
      <!-- Expand/Collapse Button -->
      <b-button
        v-if="(service.children && service.children.length > 0) || (service.actions && service.actions.length > 0)"
        variant="flat-secondary"
        size="sm"
        class="btn-icon mr-50 p-0"
        @click="toggleExpand"
      >
        <feather-icon
          :icon="isExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
          size="16"
        />
      </b-button>
      <div
        v-else
        style="width: 32px;"
      />

      <!-- Drag Handle -->
      <feather-icon
        icon="MoveIcon"
        size="18"
        class="mr-50 drag-handle cursor-move text-muted"
        style="cursor: move;"
      />

      <!-- Service Info -->
      <div class="flex-grow-1">
        <div class="d-flex align-items-center">
          <feather-icon
            v-if="service.service_type && service.service_type.icon"
            :icon="service.service_type.icon"
            size="18"
            class="mr-50"
            :style="{ color: service.service_type.color }"
          />
          <strong class="mr-1">{{ service.name }}</strong>
          <b-badge
            variant="light-secondary"
            pill
            class="mr-50"
          >
            {{ service.service_code }}
          </b-badge>
          <b-badge
            :variant="getStatusVariant(service.status)"
            pill
            class="mr-50"
          >
            {{ formatStatus(service.status) }}
          </b-badge>
          <b-badge
            v-if="service.priority"
            :variant="getPriorityVariant(service.priority)"
            pill
            class="mr-50"
          >
            P{{ service.priority }}
          </b-badge>
        </div>
        <div class="text-muted small mt-25">
          <span v-if="service.description">{{ service.description }}</span>
          <span class="ml-1">
            • {{ service.actions_count || 0 }} actions
            • {{ service.children_count || 0 }} children
          </span>
          <span
            v-if="service.completion_percentage !== undefined"
            class="ml-1"
          >
            • {{ service.completion_percentage }}% complete
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div class="ml-auto">
        <b-dropdown
          variant="flat-secondary"
          size="sm"
          no-caret
          right
          class="mr-50"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
            />
          </template>
          <b-dropdown-item @click="$emit('edit', service)">
            <feather-icon
              icon="EditIcon"
              size="16"
              class="mr-50"
            />
            <span>Edit Service</span>
          </b-dropdown-item>
          <b-dropdown-item @click="$emit('add-child', service)">
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-50"
            />
            <span>Add Child Service</span>
          </b-dropdown-item>
          <b-dropdown-item @click="$emit('add-action', service)">
            <feather-icon
              icon="ZapIcon"
              size="16"
              class="mr-50"
            />
            <span>Add Action</span>
          </b-dropdown-item>
          <b-dropdown-item @click="$emit('duplicate', service)">
            <feather-icon
              icon="CopyIcon"
              size="16"
              class="mr-50"
            />
            <span>Duplicate Service</span>
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item @click="$emit('move-up', service)">
            <feather-icon
              icon="ArrowUpIcon"
              size="16"
              class="mr-50"
            />
            <span>Move Up</span>
          </b-dropdown-item>
          <b-dropdown-item @click="$emit('move-down', service)">
            <feather-icon
              icon="ArrowDownIcon"
              size="16"
              class="mr-50"
            />
            <span>Move Down</span>
          </b-dropdown-item>
          <b-dropdown-divider />
          <b-dropdown-item
            variant="danger"
            @click="$emit('delete', service)"
          >
            <feather-icon
              icon="TrashIcon"
              size="16"
              class="mr-50"
            />
            <span>Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <!-- Actions & Children -->
    <b-collapse v-model="isExpanded">
      <div class="ml-2 pl-2 mt-1">
        <!-- Service Actions -->
        <div
          v-if="service.actions && service.actions.length > 0"
          class="mb-1"
        >
          <div
            v-for="action in service.actions"
            :key="`action-${action.id}`"
            class="action-item d-flex align-items-center py-50 px-1 rounded"
          >
            <feather-icon
              icon="ZapIcon"
              size="14"
              class="mr-50 text-warning"
            />
            <div class="flex-grow-1">
              <strong class="text-sm">{{ action.name }}</strong>
              <div class="text-muted text-xs">
                <span v-if="action.estimated_hours">{{ action.estimated_hours }}h</span>
                <span v-if="action.estimated_hours && action.default_responsibility_type"> • </span>
                <span>{{ formatResponsibilityType(action.default_responsibility_type) }}</span>
              </div>
            </div>
            <div class="action-buttons ml-1">
              <b-button
                v-b-tooltip.hover
                variant="flat-info"
                size="sm"
                class="btn-icon p-25"
                title="View Action"
                @click="$emit('view-action', action)"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="14"
                />
              </b-button>
              <b-button
                v-b-tooltip.hover
                variant="flat-primary"
                size="sm"
                class="btn-icon p-25"
                title="Edit Action"
                @click="$emit('edit-action', action)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="14"
                />
              </b-button>
              <b-button
                v-b-tooltip.hover
                variant="flat-success"
                size="sm"
                class="btn-icon p-25"
                title="Duplicate Action"
                @click="$emit('duplicate-action', action)"
              >
                <feather-icon
                  icon="CopyIcon"
                  size="14"
                />
              </b-button>
            </div>
          </div>
        </div>

        <!-- Child Services Drop Zone -->
        <div
          class="children-container border-left"
          :class="{ 'has-children': service.children && service.children.length > 0, 'empty-container': !service.children || service.children.length === 0 }"
        >
          <!-- Draggable children list - always present to accept drops -->
          <draggable
            :list="serviceChildren"
            :group="{ name: 'services', pull: true, put: true }"
            :animation="200"
            handle=".drag-handle"
            class="children-list"
            :class="{ 'empty-list': !service.children || service.children.length === 0 }"
            @end="handleDragEnd"
            @add="handleChildAdded"
            @change="handleDragChange"
          >
            <service-tree-item
              v-for="child in service.children"
              v-if="service.children && service.children.length > 0"
              :key="`service-${child.id}`"
              :service="child"
              @edit="$emit('edit', $event)"
              @delete="$emit('delete', $event)"
              @add-child="$emit('add-child', $event)"
              @add-action="$emit('add-action', $event)"
              @duplicate="$emit('duplicate', $event)"
              @move-up="$emit('move-up', $event)"
              @move-down="$emit('move-down', $event)"
              @view-action="$emit('view-action', $event)"
              @edit-action="$emit('edit-action', $event)"
              @duplicate-action="$emit('duplicate-action', $event)"
              @tree-updated="$emit('tree-updated', $event)"
            />
          </draggable>

          <!-- Empty drop zone indicator - shown when no children -->
          <div
            v-if="!service.children || service.children.length === 0"
            class="empty-drop-zone-indicator"
          >
            <small class="text-muted">
              <feather-icon
                icon="MoveIcon"
                size="14"
                class="mr-25"
              />
              Drop services here to make them children
            </small>
          </div>
        </div>
      </div>
    </b-collapse>
  </b-list-group-item>
</template>

<script>
import {
  BListGroupItem,
  BButton,
  BBadge,
  BCollapse,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  VBTooltip,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { formatResponsibilityType } from '@/utils/responsibilityType'

export default {
  name: 'ServiceTreeItem',
  components: {
    BListGroupItem,
    BButton,
    BBadge,
    BCollapse,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    draggable,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    service: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isExpanded: false,
      isDragOver: false,
    }
  },
  computed: {
    serviceChildren() {
      // Ensure children array exists for draggable
      if (!this.service.children) {
        this.$set(this.service, 'children', [])
      }
      return this.service.children
    },
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    handleDragEnd() {
      // Emit event to parent to update the tree
      this.$emit('tree-updated')
    },
    handleChildAdded() {
      // Ensure parent is expanded when a child is added
      if (!this.isExpanded) {
        this.isExpanded = true
      }
      this.$emit('tree-updated')
    },
    handleDragChange(evt) {
      // Auto-expand when dragging over this service's children area
      if (evt && (evt.added || evt.moved)) {
        if (!this.isExpanded) {
          this.isExpanded = true
        }
      }
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },
    formatStatus(status) {
      return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'
    },
    getPriorityVariant(priority) {
      if (priority <= 2) return 'success'
      if (priority === 3) return 'warning'
      return 'danger'
    },
    formatResponsibilityType(type) {
      return formatResponsibilityType(type, '')
    },
  },
}
</script>

<style scoped>
.border-left {
  border-left: 2px solid #e0e0e0;
}

.action-item {
  background-color: #f8f8f8;
  margin-bottom: 4px;
  transition: all 0.2s;
}

.action-item:hover {
  background-color: #f0f0f0;
}

.action-buttons {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.action-item:hover .action-buttons {
  opacity: 1;
}

.text-xs {
  font-size: 0.75rem;
}

.text-sm {
  font-size: 0.875rem;
}

.p-25 {
  padding: 0.25rem !important;
}

.cursor-move {
  cursor: move !important;
}

.drag-handle {
  opacity: 0.5;
  transition: opacity 0.2s, color 0.2s;
}

.drag-handle:hover {
  opacity: 1;
  color: #7367f0 !important;
}

.service-item {
  position: relative;
  transition: all 0.2s;
  margin-bottom: 0.25rem;
}

.service-item.drop-zone-active {
  background-color: #f0f4ff;
  border-left: 3px solid #7367f0 !important;
}

.children-container {
  border-left: 2px solid #e0e0e0;
  padding-left: 1.5rem;
  margin-left: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  transition: all 0.2s;
  position: relative;
}

.children-container.empty-container {
  min-height: 4rem;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.children-container.has-children {
  min-height: 0.5rem;
}

.children-list {
  min-height: 0.5rem;
  position: relative;
}

.children-list.empty-list {
  min-height: 3rem;
}

.empty-drop-zone-indicator {
  padding: 1rem;
  border: 2px dashed #d0d0d0;
  border-radius: 4px;
  text-align: center;
  background-color: #fafafa;
  transition: all 0.2s;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 0.5rem;
  pointer-events: none;
  position: relative;
  z-index: 1;
}

.empty-drop-zone-indicator:hover {
  border-color: #7367f0;
  background-color: #f0f4ff;
}

/* Visual feedback when dragging over empty drop zone */
.children-list.sortable-drag-over + .empty-drop-zone-indicator,
.children-container:has(.sortable-ghost) .empty-drop-zone-indicator {
  border-color: #7367f0;
  background-color: #f0f4ff;
  border-width: 2px;
}

/* Make the children list area always accept drops */
.children-list {
  min-height: 3rem;
  padding: 0.25rem;
  border: 2px dashed transparent;
  border-radius: 4px;
  transition: all 0.2s;
}

.children-list.empty-list {
  border-color: #e0e0e0;
  background-color: #fafafa;
}

.children-list.sortable-drag-over {
  border-color: #7367f0;
  background-color: #f0f4ff;
  border-width: 2px;
}

/* Visual feedback when dragging over drop zone */
.children-list.sortable-ghost + .empty-drop-zone,
.children-list:has(.sortable-ghost) + .empty-drop-zone {
  border-color: #7367f0;
  background-color: #f0f4ff;
  border-width: 2px;
}

.children-list {
  min-height: 0.5rem;
  position: relative;
}

/* Make the entire children list area a drop target */
.children-list::before {
  content: '';
  position: absolute;
  top: -0.5rem;
  left: -1.5rem;
  right: -0.5rem;
  bottom: -0.5rem;
  z-index: 0;
  pointer-events: none;
}

/* Visual feedback for drop zones during drag */
.sortable-ghost ~ .empty-drop-zone,
.children-list:active .empty-drop-zone {
  border-color: #7367f0;
  background-color: #f0f4ff;
}

/* Make drop zone more visible */
.children-container {
  min-height: 3.5rem;
}

.children-container.has-children {
  min-height: 1rem;
}

/* Draggable styles */
.sortable-ghost {
  opacity: 0.4;
  background: #f0f0f0;
}

.sortable-drag {
  opacity: 0.8;
}

.sortable-ghost .service-item {
  background-color: #e8e8e8;
}

/* Visual feedback for drop zones */
.service-item.sortable-ghost {
  border: 2px dashed #7367f0;
}

/* Make sure children area is always visible when expanded */
.border-left {
  position: relative;
}

.border-left::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e0e0e0;
}
</style>
